import { afterRenderEffect, Component, effect, ElementRef, inject, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, signal, ViewChild } from '@angular/core';
import { SocialScreeningService } from '../../services/social-screening/social-screening.service';
import { CreatorService } from '../../services/creator/creator.service';
import { MagicSearchService } from '../creators/service/magic-search/magic-search.service';
import { CollaboratorService } from '../../services/collaborator/collaborator.service';
import { BriefService } from '../../services/brief/brief.service';
import { BriefApplicationService } from '../../services/brief-application/brief-application.service';
import { ALL_APPLICATION_STATUSES, briefApplicationStatusesByCreator } from './creators-v4-filter.helpers';
import { FindCreatorsFilters } from './molecules/find-creators-filters/find-creators-filters';
import { BlueGoldSearchItemComponent } from './molecules/blue-gold-search-item/blue-gold-search-item.component';
import { CollaboratorsComponent } from '../creators/molecules/collaborators/collaborators.component';
import { CreatorProfileComponent } from '../creators/molecules/creator-profile/creator-profile.component';
import { CreatorsForListComponent } from '../screening-briefs/molecules/creators-for-list/creators-for-list.component';
import { VideoCarouselComponent } from '../../atoms/video-carousel/video-carousel.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LoadingInlineComponent } from '../../atoms/loading-inline/loading-inline.component';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FilterCreatorsDto, FilterItemDto } from '../../common/general-types/filters.dto';
import { Location } from '@angular/common';
import { creatorSortOptions } from '../../common/app-config/filter-models/sort';
import { CreatorResponseDto } from '../../common/general-types/creators.dto';

/** Brief IDs are TypeORM UUIDs; Firebase creator UIDs are not. */
const BRIEF_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Firebase Auth UIDs are 28 characters and case-sensitive. */
const CREATOR_FIREBASE_UID_RE = /^[A-Za-z0-9]{28}$/;

function isCreatorFirebaseUid(value: string): boolean {
  return CREATOR_FIREBASE_UID_RE.test(value);
}

function isBriefUuid(value: string | undefined | null): boolean {
  return !!value && BRIEF_UUID_RE.test(value);
}

@Component({
  selector: 'app-creators-v4-filter',
  standalone: true,
  imports: [FindCreatorsFilters, BlueGoldSearchItemComponent, CollaboratorsComponent,
    CreatorProfileComponent, CreatorsForListComponent, VideoCarouselComponent,
    LoadingInlineComponent, RouterLink],
  templateUrl: './creators-v4-filter.component.html',
  styleUrl: './creators-v4-filter.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CreatorsV4FilterComponent implements OnInit, OnDestroy {

  @ViewChild('collaboratorsFab') private collaboratorsFab?: ElementRef<HTMLElement>;
  @ViewChild('resultsScroll') private resultsScroll?: ElementRef<HTMLElement>;

  fabAttention = signal(false);
  badgeAttention = signal(false);

  /** Services */
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  protected magicSearchService = inject(MagicSearchService);
  private creatorService = inject(CreatorService);
  private briefService = inject(BriefService);
  private briefApplicationService = inject(BriefApplicationService);
  public collaboratorsService = inject(CollaboratorService);
  public socialScreeningService = inject(SocialScreeningService);
  /** Services - END */

  /** Present only when the path segment is a brief UUID (not a creator Firebase UID). */
  briefId: string | undefined;

  private keywordInput$ = new Subject<string>();
  private subscriptions = new Subscription();
  private uidLookupSub?: Subscription;
  private isPaginating = false;
  private suppressCreatorUrlSync = true;
  private pendingScrollRestore = false;
  private activeFlyDotAnimation: Animation | null = null;
  private activeFlyDotElement: HTMLElement | null = null;
  private attentionResetTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastHandledSelectionGuideNonce = 0;

  constructor() {
    effect(() => {
      const creatorId = this.magicSearchService.portfolioCreatorId();
      if (this.suppressCreatorUrlSync) {
        return;
      }
      this.syncCreatorIdToUrl(creatorId);
    });

    effect(() => {
      const guide = this.collaboratorsService.selectionGuide();
      if (!guide || guide.nonce === this.lastHandledSelectionGuideNonce) {
        return;
      }
      this.lastHandledSelectionGuideNonce = guide.nonce;
      this.runSelectionGuideAnimation(guide.originX, guide.originY);
    });

    afterRenderEffect(() => {
      if (!this.pendingScrollRestore) {
        return;
      }
      const results = this.magicSearchService.blueSearchResults();
      const el = this.resultsScroll?.nativeElement;
      if (!results?.length || !el) {
        return;
      }
      el.scrollTop = this.magicSearchService.blueSearchResultsScrollTop;
      this.pendingScrollRestore = false;
    });
  }

  public hasActiveFilters(): boolean {
    const filters = this.magicSearchService.appliedFilters;
    if (!filters) {
      return false;
    }
    return Object.keys(filters).length > 0;
  }

  /** Creators already on the brief get a status badge instead of an Invite button. */
  private loadBriefApplicationStatuses(briefId: string): void {
    this.subscriptions.add(
      this.briefApplicationService.getBroadCastApplicants(briefId, ALL_APPLICATION_STATUSES).subscribe({
        next: (applications) => {
          if (this.collaboratorsService.briefId() !== briefId) {
            return;
          }
          this.collaboratorsService.briefApplicationStatuses.set(briefApplicationStatusesByCreator(applications));
        },
        error: (err) => console.warn('Error loading brief applicants: ', err),
      }),
    );
  }

  ngOnInit(): void {
    const pathId = this.route.snapshot.params['briefId'] as string | undefined;
    const creatorIdFromQuery = this.route.snapshot.queryParamMap.get('creatorId') ?? undefined;
    const creatorIdFromPath = pathId && !isBriefUuid(pathId) ? pathId : undefined;

    this.briefId = isBriefUuid(pathId) ? pathId : undefined;

    this.subscriptions.add(
      this.creatorService.getShoutCreatorInterests().subscribe({
        next: (interests: unknown) => {
          this.creatorService.shoutCreatorInterests = interests as { id: string; tagName: string }[];
        },
        error: (err) => {
          console.warn('Error getting interests: ', err);
        },
      }),
    );

    this.collaboratorsService.briefApplicationStatuses.set(new Map());
    if (this.briefId) {
      this.collaboratorsService.briefId.set(this.briefId);
      this.magicSearchService.findCreatorsBriefId.set(this.briefId);
      this.loadBriefApplicationStatuses(this.briefId);
      this.subscriptions.add(
        this.briefService.getBriefById(this.briefId).subscribe({
          next: (brief) => {
            this.magicSearchService.findCreatorsBriefAudience.set(brief.brief_audience);
            this.startInitialBlueSearch();
          },
          error: () => {
            this.magicSearchService.findCreatorsBriefAudience.set(undefined);
            this.startInitialBlueSearch();
          },
        })
      );
    } else {
      this.magicSearchService.findCreatorsBriefId.set(undefined);
      this.magicSearchService.findCreatorsBriefAudience.set(undefined);
      this.startInitialBlueSearch();
    }

    const creatorIdToOpen = creatorIdFromQuery ?? creatorIdFromPath;
    if (creatorIdToOpen) {
      this.openCreatorProfileFromUrl(creatorIdToOpen);
    } else {
      this.magicSearchService.portfolioCreatorId.set(undefined);
      this.magicSearchService.portfolioCreator.set(undefined);
      this.suppressCreatorUrlSync = false;
    }

    const keywordSub = this.keywordInput$
      .pipe(debounceTime(300))
      .subscribe((value) => this.applyKeywordSearch(value));

    this.subscriptions.add(keywordSub);
  }

  ngOnDestroy(): void {
    const scrollEl = this.resultsScroll?.nativeElement;
    if (scrollEl) {
      this.magicSearchService.blueSearchResultsScrollTop = scrollEl.scrollTop;
    }
    this.magicSearchService.findCreatorsBriefAudience.set(undefined);
    this.magicSearchService.findCreatorsBriefId.set(undefined);
    this.subscriptions.unsubscribe();
    this.clearActiveFlyDot();
    if (this.attentionResetTimeoutId !== null) {
      clearTimeout(this.attentionResetTimeoutId);
      this.attentionResetTimeoutId = null;
    }
  }

  private runSelectionGuideAnimation(originX: number, originY: number): void {
    const fabHost = this.collaboratorsFab?.nativeElement;
    if (!fabHost) {
      this.triggerFabAttention();
      return;
    }

    const fabRect = fabHost.getBoundingClientRect();
    const targetX = fabRect.left + fabRect.width / 2;
    const targetY = fabRect.top + fabRect.height / 2;

    if (this.prefersReducedMotion()) {
      this.triggerFabAttention();
      return;
    }

    this.clearActiveFlyDot();

    const dot = document.createElement('span');
    dot.setAttribute('aria-hidden', 'true');
    dot.className = 'collaborators-fly-dot';
    dot.style.position = 'fixed';
    dot.style.zIndex = '1000';
    dot.style.width = '1.5rem';
    dot.style.height = '1.5rem';
    dot.style.borderRadius = '9999px';
    dot.style.backgroundColor = 'var(--primary)';
    dot.style.boxShadow = '0 0 0 8px color-mix(in srgb, var(--primary) 25%, transparent)';
    dot.style.pointerEvents = 'none';
    dot.style.transform = 'translate(-50%, -50%)';
    dot.style.left = `${originX}px`;
    dot.style.top = `${originY}px`;
    document.body.appendChild(dot);
    this.activeFlyDotElement = dot;

    const deltaX = targetX - originX;
    const deltaY = targetY - originY;

    this.activeFlyDotAnimation = dot.animate(
      [
        { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
        { transform: `translate(calc(-50% + ${deltaX}px), calc(-50% + ${deltaY}px)) scale(0.6)`, opacity: 0.85 },
      ],
      {
        duration: 650,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      },
    );

    this.activeFlyDotAnimation.onfinish = () => {
      this.clearActiveFlyDot();
      this.triggerFabAttention();
    };
    this.activeFlyDotAnimation.oncancel = () => {
      this.clearActiveFlyDot();
    };
  }

  private triggerFabAttention(): void {
    this.fabAttention.set(false);
    this.badgeAttention.set(false);

    queueMicrotask(() => {
      this.fabAttention.set(true);
      this.badgeAttention.set(true);
    });

    if (this.attentionResetTimeoutId !== null) {
      clearTimeout(this.attentionResetTimeoutId);
    }

    this.attentionResetTimeoutId = setTimeout(() => {
      this.fabAttention.set(false);
      this.badgeAttention.set(false);
      this.attentionResetTimeoutId = null;
    }, 620);
  }

  private clearActiveFlyDot(): void {
    this.activeFlyDotAnimation?.cancel();
    this.activeFlyDotAnimation = null;
    this.activeFlyDotElement?.remove();
    this.activeFlyDotElement = null;
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private openCreatorProfileFromUrl(creatorId: string): void {
    this.subscriptions.add(
      this.creatorService.getCreatorById(creatorId).subscribe({
        next: (creator) => {
          this.magicSearchService.portfolioCreatorId.set(creator.blazeFirebaseUid);
          this.magicSearchService.portfolioCreator.set(creator);
          this.suppressCreatorUrlSync = false;
        },
        error: (err) => {
          console.warn('Unable to open creator profile from URL: ', err);
          this.suppressCreatorUrlSync = false;
          this.syncCreatorIdToUrl(undefined);
        },
      }),
    );
  }

  private syncCreatorIdToUrl(creatorId: string | undefined): void {
    const briefId = this.briefId;

    if (briefId) {
      const queryParams = creatorId ? { creatorId } : {};
      void this.router.navigate(['/creators-v4-filter', briefId], {
        queryParams,
        replaceUrl: true,
      });
      return;
    }

    // Keep creator id in query params and update the URL without a router
    // navigation. Switching between `/creators-v4-filter` and
    // `/creators-v4-filter/:id` remounts this page and re-runs the blue
    // search, which clears the list and resets scroll when the profile closes.
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/creators-v4-filter'], {
        queryParams: creatorId ? { creatorId } : {},
      }),
    );
    this.location.replaceState(url);
  }

  private startInitialBlueSearch(): void {
    const defaultSort = creatorSortOptions[0];
    const currentUiFilters = this.magicSearchService.filtersObjectSignal() as { sort?: FilterItemDto };
    const currentAppliedFilters = this.magicSearchService.appliedFilters ?? {};

    const matchingSort =
      currentUiFilters.sort
      ?? creatorSortOptions.find(
        (option) =>
          option.value === currentAppliedFilters.sortField
          && option.direction === currentAppliedFilters.sortOrder,
      )
      ?? defaultSort;

    const nextAppliedFilters = {
      ...currentAppliedFilters,
      sortField: currentAppliedFilters.sortField ?? (matchingSort.value as string),
      sortOrder: currentAppliedFilters.sortOrder ?? (matchingSort.direction as string),
    };

    this.magicSearchService.appliedFilters = nextAppliedFilters;
    this.magicSearchService.filtersObjectSignal.update((current) => ({
      ...current,
      sort: (current as { sort?: FilterItemDto }).sort ?? matchingSort,
    }));

    // Returning from admin user-profile remounts this page; reuse cached results
    // so the list (and saved scroll offset) are not wiped.
    const existingResults = this.magicSearchService.blueSearchResults();
    if (existingResults && existingResults.length > 0 && !this.magicSearchService.isSearching()) {
      this.pendingScrollRestore = true;
      return;
    }

    this.magicSearchService.kickOffBlueSearch(nextAppliedFilters);
  }

  onKeywordSearchInput(value: string): void {
    this.keywordInput$.next(value ?? '');
  }

  applyKeywordSearch(value: string): void {
    const trimmed = value?.trim() ?? '';
    this.magicSearchService.searchQuery.set(trimmed);

    this.magicSearchService.filtersObjectSignal.update(current => {
      const updated: FilterCreatorsDto = { ...current };
      if (trimmed.length > 0) {
        updated.searchTerm = trimmed;
      } else {
        delete updated.searchTerm;
      }
      return updated;
    });

    if (isCreatorFirebaseUid(trimmed)) {
      this.magicSearchService.appliedFilters = {
        ...this.magicSearchService.appliedFilters,
        searchTerm: trimmed,
      };
      this.lookupCreatorByFirebaseUid(trimmed);
      return;
    }

    this.uidLookupSub?.unsubscribe();
    this.uidLookupSub = undefined;

    const updatedFilters = {
      ...this.magicSearchService.appliedFilters,
      searchTerm: trimmed.length > 0 ? trimmed.toLowerCase() : undefined
    };

    this.magicSearchService.appliedFilters = updatedFilters;
    this.magicSearchService.kickOffBlueSearch(updatedFilters);
  }

  private lookupCreatorByFirebaseUid(uid: string): void {
    this.uidLookupSub?.unsubscribe();
    this.magicSearchService.cancelInFlightBlueSearch();
    this.magicSearchService.pageNumber.set(0);
    this.magicSearchService.blueSearchResultsScrollTop = 0;
    this.magicSearchService.blueSearchResults.set(undefined);
    this.magicSearchService.isSearching.set(true);
    this.magicSearchService.loadingResults.set(true);

    this.uidLookupSub = this.creatorService.getCreatorById(uid).subscribe({
      next: (creator) => {
        const visible = this.creatorMatchesFindCreatorsModeration(creator) ? [creator] : [];
        this.magicSearchService.blueSearchResults.set(visible);
        this.magicSearchService.isSearching.set(false);
        this.magicSearchService.loadingResults.set(false);
      },
      error: () => {
        this.magicSearchService.blueSearchResults.set([]);
        this.magicSearchService.isSearching.set(false);
        this.magicSearchService.loadingResults.set(false);
      },
    });
    this.subscriptions.add(this.uidLookupSub);
  }

  private creatorMatchesFindCreatorsModeration(creator: CreatorResponseDto): boolean {
    const allowed = this.magicSearchService.applyFindCreatorsModerationFilter(
      {} as FilterCreatorsDto,
    ).limitModerationStatus;
    if (allowed == null) {
      return true;
    }
    return allowed.includes(creator.moderationStaus);
  }

  onResultsScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    this.magicSearchService.blueSearchResultsScrollTop = target.scrollTop;

    const threshold = 100;
    const atBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;

    if (!atBottom) {
      return;
    }

    if (isCreatorFirebaseUid(this.magicSearchService.searchQuery()?.trim() ?? '')) {
      return;
    }

    if (this.isPaginating || this.magicSearchService.isSearching()) {
      return;
    }

    if (!this.magicSearchService.blueSearchResults() || this.magicSearchService.blueSearchResults()!.length === 0) {
      return;
    }

    this.isPaginating = true;

    this.magicSearchService.pageNumber.update(current => current + 1);
    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters, true);

    setTimeout(() => {
      this.isPaginating = false;
    }, 500);
  }

  toolbarSortSelectValue(): string {
    const sort = (this.magicSearchService.filtersObjectSignal() as { sort?: FilterItemDto }).sort;
    if (sort?.id !== undefined && sort.id !== null && `${sort.id}` !== '') {
      return String(sort.id);
    }
    return '1';
  }

  isToolbarSortOptionSelected(sortOption: FilterItemDto): boolean {
    return String(sortOption.id) === this.toolbarSortSelectValue();
  }

  toolbarSortDisplayName(): string {
    const fromSignal = (this.magicSearchService.filtersObjectSignal() as { sort?: FilterItemDto }).sort;
    if (fromSignal?.name) {
      return fromSignal.name;
    }
    const idVal = this.toolbarSortSelectValue();
    const opts =
      this.magicSearchService.fancyActiveFilters?.sortOptions ??
      this.magicSearchService.activeFilters.sortOptions;
    const match = opts?.find((o) => String(o.id) === idVal);
    return match?.name ?? 'Sort';
  }

  onToolbarSortOptionSelected(sortOption: FilterItemDto): void {
    this.magicSearchService.filtersObjectSignal.update((current) => ({
      ...current,
      sort: sortOption,
    }));
    this.magicSearchService.appliedFilters = {
      ...this.magicSearchService.appliedFilters,
      sortField: sortOption.value as string,
      sortOrder: sortOption.direction as string,
    };
    this.magicSearchService.kickOffBlueSearch(this.magicSearchService.appliedFilters);
  }

  backToBrief() {
    this.location.back();
  }
}
